import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';

import { CapyBoard } from '@/app/CapyBoard';
import {
  type ActionArtRuntime,
  type ActionStripDomImageSource,
} from '@/app/actionArt';
import { createProductionActionArtRuntime } from '@/app/actionArt/production';
import { projectBattleCue } from '@/app/battleCue';
import { CapturedPieceTray } from '@/app/pieceTheme/CapturedPieceTray';
import {
  projectCapturedMaterial,
  type PieceTheme,
} from '@/app/pieceTheme/capturedMaterial';
import {
  type BeginnerCapyOpponentId,
  type CapyOpponentId,
  useGameController,
} from '@/app/useGameController';
import {
  activateAndWaitForReloadRequired,
  PWA_LIFECYCLE_EVENT,
  type PwaLifecycleDetail,
} from '@/pwa/lifecycle';
import type { BoardIntentHandler, BoardOrientation, BoardProjection } from '@/game/board';
import {
  createIndexedDbPuzzleProgressPersistence,
  createPuzzleProgressRecord,
  restorePuzzleProgress,
  type PuzzleProgressRecord,
  type PuzzleProgressPersistenceFailure,
} from '@/game/persistence';
import { type PromotionPiece, type RulesColor } from '@/game/rules';
import { createGameReviewExport } from '@/game/review';
import {
  selectBoardInputLocked,
  selectClockProjection,
  selectDisplayPosition,
  selectDisplayedMoves,
  selectLearnUndoPlies,
  toGameSessionRecord,
  type ClockSettings,
  type GameSessionState,
  type HumanColorPreference,
} from '@/game/session';
import {
  createPuzzleState,
  reconstructPuzzleSnapshot,
  reducePuzzle,
  type PuzzleAction,
  type PuzzleState,
} from '@/game/puzzles/puzzleDomain';
import { STARTER_PUZZLES } from '@/game/puzzles/starterPuzzles';
import '@/styles/app.css';

type Screen = 'home' | 'setup' | 'play' | 'puzzle' | 'review';
type ReviewExportStatus = {
  readonly kind: 'success' | 'error';
  readonly message: string;
};
type SetupMode = 'computer' | 'hot-seat';
type TimeControlId = 'none' | 'rapid' | 'blitz';
type PwaActionStatus = 'idle' | 'saving' | 'activating';
type PuzzleProgressStorageStatus =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready' }
  | { readonly phase: 'conflict'; readonly error: PuzzleProgressPersistenceFailure }
  | {
      readonly phase: 'degraded';
      readonly error: PuzzleProgressPersistenceFailure;
      /** Only a failed catalog/schema read may be explicitly discarded. */
      readonly recovery?: 'invalid-record';
    };

type PuzzleReloadPreparation =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: 'storage-not-ready' | 'persistence-failed';
      readonly error?: PuzzleProgressPersistenceFailure;
    };

const TIME_CONTROLS: ReadonlyArray<{
  readonly id: TimeControlId;
  readonly label: string;
  readonly clock?: ClockSettings;
}> = [
  { id: 'none', label: 'ไม่จับเวลา' },
  { id: 'rapid', label: '10+0', clock: { initialMs: 10 * 60_000, incrementMs: 0 } },
  { id: 'blitz', label: '5+3', clock: { initialMs: 5 * 60_000, incrementMs: 3_000 } },
];

const OPPONENT_COPY: Readonly<Record<BeginnerCapyOpponentId, { readonly name: string; readonly description: string }>> = {
  sprout: {
    name: 'ต้นกล้า',
    description: 'คาปีบารามือใหม่ ค่อย ๆ พัฒนาตัวหมากทีละตัว เหมาะกับคนที่เพิ่งหัดเล่น',
  },
  scout: {
    name: 'นักลาดตระเวน',
    description: 'มองเห็นช่องกินหมากและกับดักเป็นแล้ว เผลอทีเดียวโดนเก็บตัวหมาก',
  },
};

function lastMoveFor(state: GameSessionState) {
  const moves = state.review
    ? state.acceptedMoves.slice(0, state.review.ply)
    : state.acceptedMoves;
  const last = moves.at(-1);
  return last === undefined
    ? undefined
    : { from: last.uci.slice(0, 2), to: last.uci.slice(2, 4) };
}

function describeLastMove(lastMove: BoardProjection['lastMove']): string | undefined {
  return lastMove === undefined
    ? undefined
    : `ตาเดินล่าสุด: จากช่อง ${lastMove.from} ไปช่อง ${lastMove.to}`;
}

function terminalAnnouncementKey(state: GameSessionState): string | undefined {
  if (!state.result.terminal) {
    return undefined;
  }
  const winner = 'winner' in state.result ? state.result.winner : '';
  return `${state.result.reason}:${state.result.token}:${winner}`;
}

function mayControlBoard(state: GameSessionState): boolean {
  return state.mode.kind === 'hot-seat' || state.position.turn === state.mode.humanColor;
}

function gameProjection(
  state: GameSessionState,
  orientation: BoardOrientation,
  dialogOpen = false,
): BoardProjection {
  const snapshot = selectDisplayPosition(state);
  return {
    snapshot,
    interactive: !dialogOpen && mayControlBoard(state) && !selectBoardInputLocked(state),
    promotionPending: state.promotionPending !== undefined,
    review: state.review !== undefined,
    orientation,
    coordinates: true,
    lastMove: lastMoveFor(state),
    ...(snapshot.checkedColor === undefined
      ? {}
      : { checkedColor: snapshot.checkedColor === 'w' ? 'white' : 'black' }),
  };
}

const THAI_PIECE_BY_SAN: Readonly<Record<string, string>> = {
  K: 'คิง',
  Q: 'ควีน',
  R: 'เรือ',
  B: 'บิชอป',
  N: 'ม้า',
};

const THAI_PROMOTION: Readonly<Record<string, string>> = {
  q: 'ควีน',
  r: 'เรือ',
  b: 'บิชอป',
  n: 'ม้า',
};

const UCI_INPUT_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

function describeAcceptedMove(state: GameSessionState): string {
  const move = state.acceptedMoves.at(-1);
  if (move === undefined) {
    return '';
  }

  const initialTurn = state.position.initialFen.split(/\s+/)[1] === 'b' ? 'b' : 'w';
  const moveColor = state.acceptedMoves.length % 2 === 1
    ? initialTurn
    : initialTurn === 'w' ? 'b' : 'w';
  const piece = move.san.startsWith('O-O')
    ? 'คิง'
    : THAI_PIECE_BY_SAN[move.san[0] ?? ''] ?? 'เบี้ย';
  const promotion = move.uci[4] === undefined
    ? ''
    : ` เลื่อนขั้นเป็น${THAI_PROMOTION[move.uci[4]] ?? 'ตัวหมาก'}`;
  const capture = move.san.includes('x') ? ' จับหมาก' : '';
  const check = state.position.checkedColor === undefined
    ? ''
    : ` รุกคิงสี${state.position.checkedColor === 'w' ? 'ขาว' : 'ดำ'}`;

  return `ตาที่ ${state.acceptedMoves.length}: ${piece}สี${moveColor === 'w' ? 'ขาว' : 'ดำ'} เดินจาก ${move.uci.slice(0, 2)} ไป ${move.uci.slice(2, 4)}${capture}${promotion}${check}; ${describeNextTurn(state)}`;
}

function describeGameState(state: GameSessionState): string {
  if (state.result.terminal) {
    switch (state.result.reason) {
      case 'checkmate':
        return state.result.winner === 'w' ? 'รุกจน: ฝั่งขาวชนะ' : 'รุกจน: ฝั่งดำชนะ';
      case 'timeout':
        return state.result.winner === 'w' ? 'หมดเวลา: ฝั่งขาวชนะ' : 'หมดเวลา: ฝั่งดำชนะ';
      case 'resignation':
        return state.result.winner === 'w' ? 'ยอมแพ้: ฝั่งขาวชนะ' : 'ยอมแพ้: ฝั่งดำชนะ';
      case 'draw-agreement':
        return 'เสมอ: ผู้เล่นทั้งสองฝ่ายตกลงกัน';
      case 'stalemate':
        return 'เสมอ: ไม่มีการเดินที่ถูกกติกา';
      case 'threefold-repetition':
        return 'เสมอ: ตำแหน่งซ้ำสามครั้ง';
      case 'fifty-move-rule':
        return 'เสมอ: กฎ 50 ตาเดิน';
      case 'insufficient-material':
        return 'เสมอ: วัสดุไม่พอรุกจน';
      default:
        return 'เกมเสมอ';
    }
  }
  if (state.promotionPending) {
    return 'เลือกตัวหมากที่ต้องการเลื่อนขั้น';
  }
  if (state.review) {
    return `กำลังดูตาที่ ${state.review.ply} จาก ${state.acceptedMoves.length}`;
  }
  if (state.ai.pending) {
    return 'คาปีบารากำลังคิดอยู่';
  }
  if (state.mode.kind === 'computer') {
    return state.position.turn === state.mode.humanColor ? 'ถึงตาคุณ' : 'ถึงตาคาปีบารา';
  }
  return state.position.turn === 'w' ? 'ถึงตาฝั่งขาว' : 'ถึงตาฝั่งดำ';
}

function describeNextTurn(state: GameSessionState): string {
  if (state.result.terminal) {
    return describeGameState(state);
  }
  if (state.mode.kind === 'computer') {
    return state.position.turn === state.mode.humanColor ? 'ถึงตาคุณ' : 'ถึงตาคาปีบารา';
  }
  return state.position.turn === 'w' ? 'ถึงตาฝั่งขาว' : 'ถึงตาฝั่งดำ';
}

export function boardProjectionForPuzzle(
  state: PuzzleState,
  orientation: BoardOrientation,
  promotionPending: boolean,
  inputLocked = false,
): BoardProjection {
  const authoritativeSnapshot = reconstructPuzzleSnapshot(state);
  if (authoritativeSnapshot === null) {
    throw new Error('Puzzle state could not be reconstructed from its accepted move history.');
  }
  const snapshot = Object.freeze({ ...authoritativeSnapshot, revision: state.revision });
  const last = state.moves.at(-1);
  return {
    snapshot,
    interactive: (
      state.status === 'active'
      || state.status === 'try-again'
    ) && !snapshot.result.terminal && !promotionPending && !inputLocked,
    promotionPending,
    review: false,
    orientation,
    coordinates: true,
    ...(snapshot.checkedColor === undefined
      ? {}
      : { checkedColor: snapshot.checkedColor === 'w' ? 'white' : 'black' }),
    ...(last ? { lastMove: { from: last.from, to: last.to } } : {}),
  };
}

function isPromotionMove(state: PuzzleState, from: string, to: string): boolean {
  const snapshot = reconstructPuzzleSnapshot(state);
  if (snapshot === null) {
    return false;
  }
  return snapshot.legalMoves.some((move) => move.from === from && move.to === to && move.isPromotion);
}

function createStarterPuzzleStates(): readonly PuzzleState[] {
  return Object.freeze(STARTER_PUZZLES.map((puzzle) => createPuzzleState(puzzle)));
}

function firstStarterPuzzleId(): string {
  const first = STARTER_PUZZLES[0];
  if (first === undefined) {
    throw new Error('CapyChess requires at least one bundled puzzle.');
  }
  return first.id;
}

function activePuzzleState(
  states: readonly PuzzleState[],
  activePuzzleId: string,
): PuzzleState {
  const active = states.find((state) => state.puzzle.id === activePuzzleId);
  if (active === undefined) {
    throw new Error('The active puzzle must be present in the bundled starter states.');
  }
  return active;
}

function describePuzzleProgress(state: PuzzleState): string {
  if (state.status === 'solved') {
    return 'แก้แล้ว';
  }
  if (state.status === 'revealed') {
    return 'ดูเฉลยแล้ว';
  }
  if (state.status === 'try-again' || state.moves.length > 0 || state.hintStage > 0) {
    return 'กำลังทำ';
  }
  return 'ยังไม่ได้เริ่ม';
}

function puzzleProgressFailureStatus(error: PuzzleProgressPersistenceFailure): PuzzleProgressStorageStatus {
  return error.kind === 'conflict'
    ? { phase: 'conflict', error }
    : { phase: 'degraded', error };
}

function puzzleProgressReadFailureStatus(error: PuzzleProgressPersistenceFailure): PuzzleProgressStorageStatus {
  return error.kind === 'invalid-record'
    ? { phase: 'degraded', error, recovery: 'invalid-record' }
    : puzzleProgressFailureStatus(error);
}

function PromotionDialog({
  onChoose,
  onCancel,
}: {
  readonly onChoose: (piece: PromotionPiece) => void;
  readonly onCancel: () => void;
}) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    primaryRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const keepFocusInDialog = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    const focusable = [...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )];
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) {
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [onCancel]);

  return (
    <div className="promotion-backdrop" role="presentation" onMouseDown={onCancel}>
      <section
        ref={dialogRef}
        className="promotion-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promotion-title"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={keepFocusInDialog}
      >
        <h2 id="promotion-title">เลือกการเลื่อนขั้น</h2>
        <p>เลือกตัวหมากด้วยตัวเอง คาปีบาราจะไม่เลื่อนเป็นควีนให้โดยอัตโนมัติ</p>
        <div className="promotion-actions">
          {([
            ['q', 'ควีน'],
            ['r', 'เรือ'],
            ['b', 'บิชอป'],
            ['n', 'ม้า'],
          ] as const).map(([piece, label], index) => (
            <button
              key={piece}
              ref={index === 0 ? primaryRef : undefined}
              className="choice-button"
              type="button"
              onClick={() => onChoose(piece)}
            >
              {label}
            </button>
          ))}
        </div>
        <button className="text-button" type="button" onClick={onCancel}>กลับไปเลือกตาเดิน</button>
      </section>
    </div>
  );
}

function DecisionDialog({
  dialogId,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: {
  readonly dialogId: 'resign' | 'draw-offer';
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    primaryRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const cancel = useCallback(() => {
    if (!submittingRef.current) {
      onCancel();
    }
  }, [onCancel]);

  const confirm = useCallback(() => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    onConfirm();
  }, [onConfirm]);

  const keepFocusInDialog = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    const focusable = [...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )];
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) {
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [cancel]);

  return (
    <div className="promotion-backdrop" role="presentation" onMouseDown={cancel}>
      <section
        ref={dialogRef}
        className="promotion-dialog"
        data-draw-offer-dialog={dialogId === 'draw-offer' ? true : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={`${dialogId}-description`}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={keepFocusInDialog}
      >
        <h2 id={`${dialogId}-title`}>{title}</h2>
        <p id={`${dialogId}-description`}>{description}</p>
        <div className="inline-actions">
          <button
            ref={primaryRef}
            className="primary-button"
            type="button"
            onClick={confirm}
            disabled={submitting}
          >
            {confirmLabel}
          </button>
          <button className="secondary-button" type="button" onClick={cancel} disabled={submitting}>{cancelLabel}</button>
        </div>
      </section>
    </div>
  );
}

function PuzzleProgressResetDialog({
  onConfirm,
  onCancel,
}: {
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    primaryRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const cancel = useCallback(() => {
    if (!submittingRef.current) {
      onCancel();
    }
  }, [onCancel]);

  const confirm = useCallback(() => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    onConfirm();
  }, [onConfirm]);

  const keepFocusInDialog = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    const focusable = [...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )];
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) {
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [cancel]);

  return (
    <div className="promotion-backdrop" role="presentation" onMouseDown={cancel}>
      <section
        ref={dialogRef}
        className="promotion-dialog"
        data-puzzle-progress-reset-dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="puzzle-progress-reset-title"
        aria-describedby="puzzle-progress-reset-description"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={keepFocusInDialog}
      >
        <h2 id="puzzle-progress-reset-title">เริ่มความคืบหน้าโจทย์ใหม่?</h2>
        <p id="puzzle-progress-reset-description">
          ระบบจะลบเฉพาะข้อมูลความคืบหน้าโจทย์ที่อ่านไม่ได้ หากแท็บอื่นแก้ไขเป็นข้อมูลที่ใช้ได้แล้ว ระบบจะไม่ลบข้อมูลนั้น
        </p>
        <div className="inline-actions">
          <button
            ref={primaryRef}
            className="primary-button"
            type="button"
            onClick={confirm}
            disabled={submitting}
          >
            ยืนยันเริ่มใหม่
          </button>
          <button className="secondary-button" type="button" onClick={cancel} disabled={submitting}>กลับไปก่อน</button>
        </div>
      </section>
    </div>
  );
}

function formatClock(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function GameClocks({ state, nowMs }: { readonly state: GameSessionState; readonly nowMs: number }) {
  const clock = selectClockProjection(state, nowMs);
  if (clock === undefined) {
    return null;
  }
  return (
    <section className="clock-card" aria-label="นาฬิกาเกม">
      <div className={clock.activeColor === 'w' ? 'clock-value is-active' : 'clock-value'}>
        <span>ขาว</span><strong>{formatClock(clock.whiteRemainingMs)}</strong>
      </div>
      <div className={clock.activeColor === 'b' ? 'clock-value is-active' : 'clock-value'}>
        <span>ดำ</span><strong>{formatClock(clock.blackRemainingMs)}</strong>
      </div>
    </section>
  );
}

function ResultCard({
  state,
  onRematch,
  onChangeSetup,
  onReview,
}: {
  readonly state: GameSessionState;
  readonly onRematch: () => void;
  readonly onChangeSetup: () => void;
  readonly onReview: () => void;
}) {
  return (
    <section className="result-card" aria-labelledby="result-heading">
      <h2 id="result-heading">{describeGameState(state)}</h2>
      <p>ผลการแข่งขัน {state.result.token} · ทุกตาเดินยังอยู่ในเครื่องนี้เพื่อให้คุณย้อนกลับมาดูได้</p>
      <div className="inline-actions">
        <button className="primary-button" type="button" onClick={onRematch} aria-label="เล่นซ้ำ">เล่นซ้ำ</button>
        <button className="secondary-button" type="button" onClick={onReview} aria-label="ดูตาเดิน">ดูตาเดิน</button>
        <button className="secondary-button" type="button" onClick={onChangeSetup} aria-label="เปลี่ยนการตั้งค่า">เปลี่ยนการตั้งค่า</button>
      </div>
    </section>
  );
}

export interface AppProps {
  readonly createActionArtRuntime?: () => ActionArtRuntime<ActionStripDomImageSource>;
  /** Internal test seam; production navigation intentionally exposes no Puzzle route. */
  readonly initialScreen?: Screen;
}

export function App({
  createActionArtRuntime = createProductionActionArtRuntime,
  initialScreen = 'home',
}: AppProps = {}) {
  const {
    state,
    storageStatus,
    strongOpponentStatus,
    isHydrated,
    submitMoveIntent,
    submitUci,
    choosePromotion,
    cancelPromotion,
    startHotSeat,
    startComputerGame,
    resign,
    agreeDraw,
    rematch,
    clockNowMs,
    undoLearn,
    selectReviewPly,
    exitReview,
    prepareForAppReload,
    resumeAfterAppReloadFailure,
    retryTerminalPersistence,
  } = useGameController();
  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [setupMode, setSetupMode] = useState<SetupMode>('computer');
  const [opponent, setOpponent] = useState<BeginnerCapyOpponentId>('sprout');
  const [humanColor, setHumanColor] = useState<HumanColorPreference>('w');
  const [timeControl, setTimeControl] = useState<TimeControlId>('none');
  const [orientation, setOrientation] = useState<BoardOrientation>('white');
  const [pieceTheme, setPieceTheme] = useState<PieceTheme>('capy');
  const [actionArtRuntime, setActionArtRuntime] = useState<ActionArtRuntime<ActionStripDomImageSource>>();
  const [moveInput, setMoveInput] = useState('');
  const [moveInputError, setMoveInputError] = useState('');
  const [puzzleMoveInput, setPuzzleMoveInput] = useState('');
  const [notice, setNotice] = useState('');
  const [reviewExportStatus, setReviewExportStatus] = useState<ReviewExportStatus>();
  const [puzzleStates, setPuzzleStates] = useState<readonly PuzzleState[]>(createStarterPuzzleStates);
  const [activePuzzleId, setActivePuzzleId] = useState(firstStarterPuzzleId);
  const puzzle = useMemo(
    () => activePuzzleState(puzzleStates, activePuzzleId),
    [activePuzzleId, puzzleStates],
  );
  const puzzleRef = useRef(puzzle);
  const puzzleStatesRef = useRef(puzzleStates);
  const activePuzzleIdRef = useRef(activePuzzleId);
  const puzzlePersistenceRef = useRef(createIndexedDbPuzzleProgressPersistence());
  const [puzzleProgressStorageStatus, setPuzzleProgressStorageStatus] = useState<PuzzleProgressStorageStatus>({
    phase: 'loading',
  });
  const puzzleProgressStorageStatusRef = useRef<PuzzleProgressStorageStatus>({ phase: 'loading' });
  const [puzzleProgressDirty, setPuzzleProgressDirty] = useState(false);
  const puzzleProgressDirtyRef = useRef(false);
  const [puzzleProgressVersion, setPuzzleProgressVersion] = useState(0);
  const puzzleProgressVersionRef = useRef(0);
  const [puzzleInputError, setPuzzleInputError] = useState('');
  const [puzzleProgressResetDialogOpen, setPuzzleProgressResetDialogOpen] = useState(false);
  const puzzleProgressResettingRef = useRef(false);
  const [puzzlePromotion, setPuzzlePromotion] = useState<{ readonly from: string; readonly to: string }>();
  const [resignDialogOpen, setResignDialogOpen] = useState(false);
  const [drawOfferOpen, setDrawOfferOpen] = useState(false);
  const [pwaLifecycle, setPwaLifecycle] = useState<PwaLifecycleDetail>();
  const [pwaActionStatus, setPwaActionStatus] = useState<PwaActionStatus>('idle');
  const [pwaActionError, setPwaActionError] = useState('');
  const pwaLifecycleRef = useRef<PwaLifecycleDetail | undefined>(undefined);
  const pwaActionStatusRef = useRef<PwaActionStatus>('idle');
  const previousPresentationStateRef = useRef<GameSessionState | undefined>(undefined);

  useEffect(() => {
    const runtime = createActionArtRuntime();
    setActionArtRuntime(runtime);
    return () => runtime.dispose();
  }, [createActionArtRuntime]);

  const battleCue = useMemo(
    () => screen === 'play'
      ? projectBattleCue(previousPresentationStateRef.current, state)
      : undefined,
    [screen, state],
  );

  useEffect(() => {
    if (
      state.result.terminal ||
      state.mode.kind !== 'hot-seat' ||
      state.review !== undefined ||
      state.promotionPending !== undefined
    ) {
      setDrawOfferOpen(false);
    }
  }, [state.mode.kind, state.promotionPending, state.result.terminal, state.review]);

  const transitionPwaAction = useCallback((status: PwaActionStatus) => {
    pwaActionStatusRef.current = status;
    setPwaActionStatus(status);
  }, []);

  const announceKeyboardFeedback = useCallback((message: string) => {
    setNotice(message);
  }, []);

  const updatePuzzleProgressStorageStatus = useCallback((next: PuzzleProgressStorageStatus) => {
    puzzleProgressStorageStatusRef.current = next;
    setPuzzleProgressStorageStatus(next);
  }, []);

  const markPuzzleProgressDirty = useCallback(() => {
    const nextVersion = puzzleProgressVersionRef.current + 1;
    puzzleProgressVersionRef.current = nextVersion;
    puzzleProgressDirtyRef.current = true;
    setPuzzleProgressVersion(nextVersion);
    setPuzzleProgressDirty(true);
  }, []);

  const clearPuzzleProgressDirty = useCallback((savedVersion: number) => {
    if (puzzleProgressVersionRef.current !== savedVersion) {
      return;
    }
    puzzleProgressDirtyRef.current = false;
    setPuzzleProgressDirty(false);
  }, []);

  const puzzleInteractionsAllowed = useCallback(() => (
    puzzleProgressStorageStatusRef.current.phase === 'ready'
    && pwaActionStatusRef.current === 'idle'
  ), []);

  useEffect(() => {
    const handleLifecycle = (event: Event) => {
      const detail = (event as CustomEvent<PwaLifecycleDetail>).detail;
      if (detail.phase === 'offline-ready' && pwaLifecycleRef.current !== undefined) {
        return;
      }
      pwaLifecycleRef.current = detail;
      setPwaLifecycle(detail);
      if (detail.phase !== 'offline-ready' && pwaActionStatusRef.current === 'idle') {
        setPwaActionError('');
      }
    };

    window.addEventListener(PWA_LIFECYCLE_EVENT, handleLifecycle);
    return () => window.removeEventListener(PWA_LIFECYCLE_EVENT, handleLifecycle);
  }, []);

  useEffect(() => {
    puzzleRef.current = puzzle;
  }, [puzzle]);

  useEffect(() => {
    puzzleStatesRef.current = puzzleStates;
  }, [puzzleStates]);

  useEffect(() => {
    activePuzzleIdRef.current = activePuzzleId;
  }, [activePuzzleId]);

  const restoreValidatedPuzzleProgress = useCallback((record: PuzzleProgressRecord | undefined): boolean => {
    let restoredStates: readonly PuzzleState[];
    let restoredActivePuzzleId: string;
    if (record === undefined) {
      restoredStates = createStarterPuzzleStates();
      restoredActivePuzzleId = firstStarterPuzzleId();
    } else {
      const restored = restorePuzzleProgress(record, STARTER_PUZZLES);
      if (!restored.ok) {
        return false;
      }
      restoredStates = restored.value.states;
      restoredActivePuzzleId = restored.value.activePuzzleId;
    }

    const restoredPuzzle = activePuzzleState(restoredStates, restoredActivePuzzleId);
    const nextVersion = puzzleProgressVersionRef.current + 1;
    puzzleProgressVersionRef.current = nextVersion;
    puzzleProgressDirtyRef.current = false;
    puzzleStatesRef.current = restoredStates;
    activePuzzleIdRef.current = restoredActivePuzzleId;
    puzzleRef.current = restoredPuzzle;
    setPuzzleStates(restoredStates);
    setActivePuzzleId(restoredActivePuzzleId);
    setPuzzleProgressVersion(nextVersion);
    setPuzzleProgressDirty(false);
    setPuzzlePromotion(undefined);
    setPuzzleMoveInput('');
    setPuzzleInputError('');
    return true;
  }, []);

  useEffect(() => {
    let disposed = false;
    const persistence = puzzlePersistenceRef.current;
    void persistence.read(STARTER_PUZZLES).then((loaded) => {
      if (disposed) {
        return;
      }
      if (!loaded.ok) {
        updatePuzzleProgressStorageStatus(puzzleProgressReadFailureStatus(loaded.error));
        return;
      }
      if (!restoreValidatedPuzzleProgress(loaded.value)) {
        updatePuzzleProgressStorageStatus({
          phase: 'degraded',
          error: { kind: 'invalid-record' },
          recovery: 'invalid-record',
        });
        return;
      }
      updatePuzzleProgressStorageStatus({ phase: 'ready' });
    }).catch((cause: unknown) => {
      if (!disposed) {
        updatePuzzleProgressStorageStatus({
          phase: 'degraded',
          error: { kind: 'read-failed', cause },
        });
      }
    });

    return () => {
      disposed = true;
      persistence.close();
    };
  }, [restoreValidatedPuzzleProgress, updatePuzzleProgressStorageStatus]);

  useEffect(() => {
    if (puzzleProgressStorageStatus.phase !== 'ready' || !puzzleProgressDirty) {
      return undefined;
    }

    let disposed = false;
    const savingVersion = puzzleProgressVersion;
    const record = createPuzzleProgressRecord(activePuzzleId, puzzleStates);
    void puzzlePersistenceRef.current.save(record, STARTER_PUZZLES).then((saved) => {
      if (!disposed && !saved.ok) {
        updatePuzzleProgressStorageStatus(puzzleProgressFailureStatus(saved.error));
      } else if (!disposed) {
        clearPuzzleProgressDirty(savingVersion);
      }
    }).catch((cause: unknown) => {
      if (!disposed) {
        updatePuzzleProgressStorageStatus({
          phase: 'degraded',
          error: { kind: 'write-failed', cause },
        });
      }
    });

    return () => {
      disposed = true;
    };
  }, [
    activePuzzleId,
    clearPuzzleProgressDirty,
    puzzleProgressDirty,
    puzzleProgressStorageStatus.phase,
    puzzleProgressVersion,
    puzzleStates,
    updatePuzzleProgressStorageStatus,
  ]);

  useEffect(() => {
    previousPresentationStateRef.current = state;
  }, [state]);

  const announcedMoveRef = useRef<{
    readonly gameId: string;
    readonly gameEpoch: number;
    readonly count: number;
    readonly terminalKey?: string;
  } | undefined>(undefined);

  useEffect(() => {
    const previous = announcedMoveRef.current;
    const sameGameTimeline = previous !== undefined
      && previous.gameId === state.gameId
      && previous.gameEpoch === state.gameEpoch;
    const isAcceptedAppend = sameGameTimeline
      && state.acceptedMoves.length === previous.count + 1;

    const mustClearAnnouncement = previous !== undefined && (
      !sameGameTimeline
      || state.acceptedMoves.length < previous.count
    );
    const terminalKey = terminalAnnouncementKey(state);
    const isNewTerminal = sameGameTimeline
      && terminalKey !== undefined
      && terminalKey !== previous?.terminalKey;

    if (mustClearAnnouncement) {
      setNotice('');
      setMoveInputError('');
    } else if (isAcceptedAppend) {
      setNotice(describeAcceptedMove(state));
      setMoveInputError('');
    } else if (isNewTerminal) {
      setNotice(describeGameState(state));
    }

    announcedMoveRef.current = {
      gameId: state.gameId,
      gameEpoch: state.gameEpoch,
      count: state.acceptedMoves.length,
      ...(terminalKey === undefined ? {} : { terminalKey }),
    };
  }, [state]);

  const projection = useMemo(
    () => gameProjection(state, orientation, resignDialogOpen || drawOfferOpen),
    [drawOfferOpen, resignDialogOpen, state, orientation],
  );

  const onGameMoveIntent = useCallback<BoardIntentHandler>((intent) => {
    const next = submitMoveIntent(intent);
    return gameProjection(next, orientation, resignDialogOpen || drawOfferOpen);
  }, [drawOfferOpen, orientation, resignDialogOpen, submitMoveIntent]);

  const puzzleInputLocked = puzzleProgressStorageStatus.phase !== 'ready' || pwaActionStatus !== 'idle';
  const puzzleControlsLocked = puzzleInputLocked || puzzlePromotion !== undefined;
  const puzzleCanAttempt = !puzzleControlsLocked
    && (puzzle.status === 'active' || puzzle.status === 'try-again');
  const puzzleCanRequestHint = puzzleCanAttempt
    && puzzle.hintStage < (puzzle.puzzle.teaching?.hints?.length ?? 3);
  const puzzleCanReveal = puzzleCanAttempt;

  const puzzleProjection = useMemo(
    () => boardProjectionForPuzzle(puzzle, orientation, puzzlePromotion !== undefined, puzzleInputLocked),
    [orientation, puzzle, puzzleInputLocked, puzzlePromotion],
  );

  const reduceCurrentPuzzle = useCallback((action: PuzzleAction): PuzzleState => {
    const current = puzzleRef.current;
    if (!puzzleInteractionsAllowed()) {
      return current;
    }
    const next = reducePuzzle(current, action);
    if (next !== current) {
      const nextStates = puzzleStatesRef.current.map((state) => (
        state.puzzle.id === current.puzzle.id ? next : state
      ));
      puzzleRef.current = next;
      puzzleStatesRef.current = nextStates;
      setPuzzleStates(nextStates);
      setPuzzleInputError('');
      setNotice('');
      markPuzzleProgressDirty();
    }
    if (action.type === 'attempt-move' || action.type === 'reset' || action.type === 'retry') {
      setPuzzlePromotion(undefined);
    }
    return next;
  }, [markPuzzleProgressDirty, puzzleInteractionsAllowed]);

  const applyPuzzleUci = useCallback((uci: string): PuzzleState => (
    reduceCurrentPuzzle({ type: 'attempt-move', uci })
  ), [reduceCurrentPuzzle]);

  const selectPuzzle = useCallback((puzzleId: string) => {
    if (!puzzleInteractionsAllowed() || puzzlePromotion !== undefined) {
      return;
    }
    const selected = puzzleStatesRef.current.find((state) => state.puzzle.id === puzzleId);
    if (selected === undefined) {
      return;
    }
    if (activePuzzleIdRef.current !== puzzleId) {
      activePuzzleIdRef.current = puzzleId;
      puzzleRef.current = selected;
      setActivePuzzleId(puzzleId);
      markPuzzleProgressDirty();
    }
    setPuzzlePromotion(undefined);
    setPuzzleMoveInput('');
    setPuzzleInputError('');
    setNotice('');
  }, [markPuzzleProgressDirty, puzzleInteractionsAllowed, puzzlePromotion]);

  const onPuzzleMoveIntent = useCallback<BoardIntentHandler>((intent) => {
    const current = puzzleRef.current;
    const sourcePuzzleId = puzzle.puzzle.id;
    if (
      !puzzleInteractionsAllowed()
      || puzzlePromotion !== undefined
      || sourcePuzzleId !== activePuzzleIdRef.current
      || sourcePuzzleId !== current.puzzle.id
      || intent.expectedRevision !== current.revision
    ) {
      return boardProjectionForPuzzle(
        current,
        orientation,
        puzzlePromotion !== undefined,
        !puzzleInteractionsAllowed() || puzzlePromotion !== undefined,
      );
    }
    if (isPromotionMove(current, intent.from, intent.to)) {
      setPuzzlePromotion({ from: intent.from, to: intent.to });
      return boardProjectionForPuzzle(current, orientation, true);
    }
    const next = applyPuzzleUci(`${intent.from}${intent.to}`);
    return boardProjectionForPuzzle(next, orientation, false);
  }, [applyPuzzleUci, orientation, puzzle.puzzle.id, puzzleInteractionsAllowed, puzzlePromotion]);

  const beginComputerGame = useCallback(() => {
    const clock = TIME_CONTROLS.find((option) => option.id === timeControl)?.clock;
    const next = startComputerGame({ opponent, humanColor, learnMode: true, ...(clock ? { clock } : {}) });
    setOrientation(next.mode.kind === 'computer' && next.mode.humanColor === 'b' ? 'black' : 'white');
    setMoveInput('');
    setMoveInputError('');
    setDrawOfferOpen(false);
    setScreen('play');
  }, [humanColor, opponent, startComputerGame, timeControl]);

  const beginHotSeat = useCallback(() => {
    const clock = TIME_CONTROLS.find((option) => option.id === timeControl)?.clock;
    const next = startHotSeat(clock ? { clock } : {});
    setOrientation('white');
    setMoveInput('');
    setMoveInputError('');
    setDrawOfferOpen(false);
    setScreen('play');
  }, [startHotSeat, timeControl]);

  const confirmResign = useCallback(() => {
    resign();
    setResignDialogOpen(false);
  }, [resign]);

  const confirmDrawAgreement = useCallback(() => {
    agreeDraw();
    setDrawOfferOpen(false);
  }, [agreeDraw]);

  const beginRematch = useCallback(() => {
    const next = rematch();
    if (next === state) {
      return;
    }
    setOrientation(next.mode.kind === 'computer' && next.mode.humanColor === 'b' ? 'black' : 'white');
    setDrawOfferOpen(false);
    setScreen('play');
  }, [rematch, state]);

  const submitTextMove = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = moveInput.trim().toLowerCase();
    if (!UCI_INPUT_PATTERN.test(normalized)) {
      setMoveInputError('โปรดพิมพ์ตาเดินแบบ e2e4');
      return;
    }
    const before = state;
    const next = submitUci(moveInput);
    if (next === before) {
      setMoveInputError('ตาเดินนี้ใช้ไม่ได้ในตำแหน่งนี้');
      return;
    }
    setMoveInputError('');
    setMoveInput('');
  }, [moveInput, state, submitUci]);

  const submitPuzzleTextMove = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!puzzleInteractionsAllowed() || puzzlePromotion !== undefined) {
      return;
    }
    const normalized = puzzleMoveInput.trim().toLowerCase();
    if (!UCI_INPUT_PATTERN.test(normalized)) {
      setPuzzleInputError('โปรดพิมพ์ตาเดินแบบ g1f3');
      return;
    }
    const current = puzzleRef.current;
    const from = normalized.slice(0, 2);
    const to = normalized.slice(2, 4);
    if (normalized.length === 4 && isPromotionMove(current, from, to)) {
      setPuzzleInputError('');
      setPuzzlePromotion({ from, to });
      return;
    }
    const next = applyPuzzleUci(normalized);
    if (next === current) {
      setPuzzleInputError('ตาเดินนี้ใช้ไม่ได้ในตำแหน่งนี้');
      return;
    }
    setPuzzleInputError('');
    setPuzzleMoveInput('');
  }, [applyPuzzleUci, puzzleInteractionsAllowed, puzzleMoveInput, puzzlePromotion]);

  const createCurrentReviewExport = useCallback(() => {
    const reviewExport = createGameReviewExport(toGameSessionRecord(state));
    if (!reviewExport.ok) {
      setReviewExportStatus({
        kind: 'error',
        message: 'สร้าง PGN จากเกมนี้ไม่ได้ กรุณากลับไปที่กระดานแล้วลองอีกครั้ง',
      });
      return undefined;
    }
    return reviewExport.value;
  }, [state]);

  const copyReviewPgn = useCallback(async () => {
    const reviewExport = createCurrentReviewExport();
    if (reviewExport === undefined) {
      return;
    }

    try {
      if (navigator.clipboard === undefined) {
        throw new Error('Clipboard API is unavailable.');
      }
      await navigator.clipboard.writeText(reviewExport.pgn);
      setReviewExportStatus({
        kind: 'success',
        message: 'คัดลอก PGN แล้ว พร้อมนำไปวางในแอปหมากรุกหรือโน้ตได้เลย',
      });
    } catch {
      setReviewExportStatus({
        kind: 'error',
        message: 'คัดลอกอัตโนมัติไม่ได้ กรุณาใช้ปุ่ม “ดาวน์โหลด PGN” แทน',
      });
    }
  }, [createCurrentReviewExport]);

  const downloadReviewPgn = useCallback(() => {
    const reviewExport = createCurrentReviewExport();
    if (reviewExport === undefined) {
      return;
    }

    let objectUrl: string | undefined;
    const link = document.createElement('a');
    try {
      objectUrl = URL.createObjectURL(new Blob(
        [reviewExport.download.content],
        { type: reviewExport.download.mimeType },
      ));
      link.href = objectUrl;
      link.download = reviewExport.download.filename;
      link.hidden = true;
      document.body.append(link);
      link.click();
      setReviewExportStatus({ kind: 'success', message: 'เริ่มดาวน์โหลดไฟล์ PGN แล้ว' });
    } catch {
      setReviewExportStatus({
        kind: 'error',
        message: 'ดาวน์โหลด PGN ไม่สำเร็จ กรุณาลองอีกครั้ง',
      });
    } finally {
      link.remove();
      if (objectUrl !== undefined) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  }, [createCurrentReviewExport]);

  const reloadPuzzleProgress = useCallback(async () => {
    if (puzzleProgressStorageStatusRef.current.phase !== 'conflict') {
      return;
    }
    updatePuzzleProgressStorageStatus({ phase: 'loading' });
    try {
      const loaded = await puzzlePersistenceRef.current.read(STARTER_PUZZLES);
      if (!loaded.ok) {
        updatePuzzleProgressStorageStatus(puzzleProgressReadFailureStatus(loaded.error));
        return;
      }
      if (!restoreValidatedPuzzleProgress(loaded.value)) {
        updatePuzzleProgressStorageStatus({
          phase: 'degraded',
          error: { kind: 'invalid-record' },
          recovery: 'invalid-record',
        });
        return;
      }
      updatePuzzleProgressStorageStatus({ phase: 'ready' });
    } catch (cause: unknown) {
      updatePuzzleProgressStorageStatus({
        phase: 'degraded',
        error: { kind: 'read-failed', cause },
      });
    }
  }, [restoreValidatedPuzzleProgress, updatePuzzleProgressStorageStatus]);

  const resetInvalidPuzzleProgress = useCallback(async () => {
    const currentStatus = puzzleProgressStorageStatusRef.current;
    if (
      puzzleProgressResettingRef.current
      || currentStatus.phase !== 'degraded'
      || currentStatus.recovery !== 'invalid-record'
    ) {
      return;
    }

    puzzleProgressResettingRef.current = true;
    setPuzzleProgressResetDialogOpen(false);
    updatePuzzleProgressStorageStatus({ phase: 'loading' });
    try {
      const reset = await puzzlePersistenceRef.current.resetInvalid(STARTER_PUZZLES);
      if (!reset.ok) {
        updatePuzzleProgressStorageStatus(puzzleProgressFailureStatus(reset.error));
        return;
      }
      if (!restoreValidatedPuzzleProgress(undefined)) {
        updatePuzzleProgressStorageStatus({
          phase: 'degraded',
          error: { kind: 'invalid-record' },
          recovery: 'invalid-record',
        });
        return;
      }
      updatePuzzleProgressStorageStatus({ phase: 'ready' });
    } catch (cause: unknown) {
      updatePuzzleProgressStorageStatus({
        phase: 'degraded',
        error: { kind: 'write-failed', cause },
      });
    } finally {
      puzzleProgressResettingRef.current = false;
    }
  }, [restoreValidatedPuzzleProgress, updatePuzzleProgressStorageStatus]);

  const preparePuzzleProgressForAppReload = useCallback(async (): Promise<PuzzleReloadPreparation> => {
    if (puzzleProgressStorageStatusRef.current.phase !== 'ready') {
      return { ok: false, reason: 'storage-not-ready' };
    }

    const persistence = puzzlePersistenceRef.current;
    try {
      if (puzzleProgressDirtyRef.current) {
        const savingVersion = puzzleProgressVersionRef.current;
        const record = createPuzzleProgressRecord(activePuzzleIdRef.current, puzzleStatesRef.current);
        const queuedSave = persistence.save(record, STARTER_PUZZLES);
        const [saved, flushed] = await Promise.all([queuedSave, persistence.flush()]);
        if (!saved.ok) {
          updatePuzzleProgressStorageStatus(puzzleProgressFailureStatus(saved.error));
          return { ok: false, reason: 'persistence-failed', error: saved.error };
        }
        if (!flushed.ok) {
          updatePuzzleProgressStorageStatus(puzzleProgressFailureStatus(flushed.error));
          return { ok: false, reason: 'persistence-failed', error: flushed.error };
        }
        clearPuzzleProgressDirty(savingVersion);
        return { ok: true };
      }

      const flushed = await persistence.flush();
      if (!flushed.ok) {
        updatePuzzleProgressStorageStatus(puzzleProgressFailureStatus(flushed.error));
        return { ok: false, reason: 'persistence-failed', error: flushed.error };
      }
      return { ok: true };
    } catch (cause: unknown) {
      const error: PuzzleProgressPersistenceFailure = { kind: 'write-failed', cause };
      updatePuzzleProgressStorageStatus(puzzleProgressFailureStatus(error));
      return { ok: false, reason: 'persistence-failed', error };
    }
  }, [clearPuzzleProgressDirty, updatePuzzleProgressStorageStatus]);

  const acceptPwaAction = useCallback(async () => {
    const requestedLifecycle = pwaLifecycleRef.current;
    if (requestedLifecycle === undefined || requestedLifecycle.phase === 'offline-ready') {
      return;
    }
    if (pwaActionStatusRef.current !== 'idle') {
      return;
    }

    transitionPwaAction('saving');
    setPwaActionError('');
    const preparation = await prepareForAppReload();
    if (!preparation.ok) {
      transitionPwaAction('idle');
      setPwaActionError(
        preparation.reason === 'storage-not-ready'
          ? 'ยังยืนยันการบันทึกเกมไม่ได้ กรุณารอให้เกมพร้อมแล้วลองอีกครั้ง'
          : preparation.reason === 'persistence-conflict'
            ? 'เกมนี้มีการเปลี่ยนแปลงจากแท็บอื่นแล้ว กรุณาโหลดหน้าใหม่เพื่อรับเกมล่าสุด'
          : preparation.reason === 'terminal-persistence-failed'
            ? 'ผลเกมนี้ยังบันทึกไม่สำเร็จ กรุณาลองบันทึกผลเกมอีกครั้งก่อนโหลดหน้าใหม่'
          : 'ยังดำเนินการไม่ได้ เพราะบันทึกเกมในอุปกรณ์ไม่สำเร็จ กรุณาลองอีกครั้ง',
      );
      return;
    }

    const puzzlePreparation = await preparePuzzleProgressForAppReload();
    if (!puzzlePreparation.ok) {
      resumeAfterAppReloadFailure();
      transitionPwaAction('idle');
      setPwaActionError(
        puzzlePreparation.reason === 'storage-not-ready'
          ? 'ความคืบหน้าโจทย์ในเครื่องยังยืนยันไม่ได้ กรุณารอให้เปิดข้อมูลเสร็จก่อนอัปเดต'
          : 'ความคืบหน้าโจทย์ในเครื่องยังยืนยันไม่ได้ กรุณาลองอีกครั้งก่อนอัปเดต',
      );
      return;
    }

    const latestLifecycle = pwaLifecycleRef.current;
    if (requestedLifecycle.phase === 'reload-required') {
      if (latestLifecycle?.phase !== 'reload-required') {
        resumeAfterAppReloadFailure();
        transitionPwaAction('idle');
        setPwaActionError('สถานะเวอร์ชันเปลี่ยนไประหว่างบันทึกเกม กรุณาลองอีกครั้ง');
        return;
      }
      window.location.reload();
      return;
    }

    if (latestLifecycle?.phase === 'reload-required') {
      transitionPwaAction('idle');
      setPwaActionError('');
      return;
    }
    if (latestLifecycle !== requestedLifecycle) {
      resumeAfterAppReloadFailure();
      transitionPwaAction('idle');
      setPwaActionError('สถานะอัปเดตเปลี่ยนไประหว่างบันทึกเกม กรุณาลองอีกครั้ง');
      return;
    }

    transitionPwaAction('activating');
    const activation = await activateAndWaitForReloadRequired(requestedLifecycle.activate);
    if (activation.ok) {
      transitionPwaAction('idle');
      setPwaActionError('');
      return;
    }

    resumeAfterAppReloadFailure();
    transitionPwaAction('idle');
    setPwaActionError(
      activation.reason === 'timeout'
        ? 'การติดตั้งยังไม่ตอบกลับ เกมกลับมาเล่นต่อได้แล้ว กรุณาลองอัปเดตอีกครั้ง'
        : 'ติดตั้งเวอร์ชันใหม่ไม่สำเร็จ เกมกลับมาเล่นต่อได้แล้ว กรุณาลองอีกครั้ง',
    );
  }, [
    prepareForAppReload,
    preparePuzzleProgressForAppReload,
    resumeAfterAppReloadFailure,
    transitionPwaAction,
  ]);

  const switchScreen = useCallback((next: Screen) => {
    setScreen(next);
    setNotice('');
    setDrawOfferOpen(false);
    if (next === 'review') {
      setReviewExportStatus(undefined);
    }
    if (next !== 'play' && state.review) {
      exitReview();
    }
  }, [exitReview, state.review]);

  const undoAvailable = selectLearnUndoPlies(state) > 0;
  const displayedMoves = selectDisplayedMoves(state);
  const capturedMaterial = useMemo(
    () => projectCapturedMaterial(state.position.initialFen, displayedMoves),
    [displayedMoves, state.position.initialFen],
  );
  const topTrayColor: RulesColor = orientation === 'white' ? 'b' : 'w';
  const bottomTrayColor: RulesColor = topTrayColor === 'w' ? 'b' : 'w';
  const solvedPuzzleCount = useMemo(
    () => puzzleStates.filter((candidate) => candidate.status === 'solved').length,
    [puzzleStates],
  );

  return (
    <main id="capychess-app" className="app-shell" aria-label="CapyChess">
      <header className="topbar">
        <button className="brand-button" type="button" onClick={() => switchScreen('home')}>
          <span className="brand-mark" aria-hidden="true">C</span>
          <span>CapyChess</span>
        </button>
        <nav aria-label="เมนูหลัก" className="topbar__nav">
          <button type="button" onClick={() => switchScreen('setup')} aria-label="เปิดการตั้งค่าเกม">เล่น</button>
          <button type="button" onClick={() => switchScreen('review')}>ทบทวน</button>
        </nav>
      </header>

      {pwaLifecycle !== undefined && (
        <section className="pwa-notice" aria-live="polite" aria-atomic="true">
          <div>
            <strong>
              {pwaLifecycle.phase === 'offline-ready' && 'พร้อมเล่นออฟไลน์แล้ว'}
              {pwaLifecycle.phase === 'update-ready' && 'มี CapyChess เวอร์ชันใหม่'}
              {pwaLifecycle.phase === 'reload-required' && 'เวอร์ชันใหม่ติดตั้งแล้ว'}
            </strong>
            <p>
              {pwaLifecycle.phase === 'offline-ready'
                && 'เปิดเกมนี้ได้แม้ไม่มีอินเทอร์เน็ต'}
              {pwaLifecycle.phase === 'update-ready'
                && 'บันทึกเกมก่อนติดตั้ง แล้วคุณเลือกเวลาโหลดหน้าใหม่ได้'}
              {pwaLifecycle.phase === 'reload-required'
                && 'บันทึกเกมแล้วโหลดหน้าใหม่เมื่อคุณพร้อม'}
            </p>
            {pwaActionError !== '' && <p className="pwa-notice__error" role="alert">{pwaActionError}</p>}
          </div>
          {pwaLifecycle.phase === 'offline-ready' ? (
            <button
              className="text-button"
              type="button"
              onClick={() => {
                pwaLifecycleRef.current = undefined;
                setPwaLifecycle(undefined);
              }}
            >
              รับทราบ
            </button>
          ) : (
            <button
              className="secondary-button"
              type="button"
              onClick={() => void acceptPwaAction()}
              disabled={pwaActionStatus !== 'idle'}
            >
              {pwaActionStatus === 'saving' && 'กำลังบันทึกเกม…'}
              {pwaActionStatus === 'activating' && 'กำลังติดตั้ง…'}
              {pwaActionStatus === 'idle' && pwaLifecycle.phase === 'update-ready'
                && 'บันทึกเกมและติดตั้ง'}
              {pwaActionStatus === 'idle' && pwaLifecycle.phase === 'reload-required'
                && 'บันทึกเกมและโหลดใหม่'}
            </button>
          )}
        </section>
      )}

      {storageStatus.phase === 'conflict' && (
        <section className="storage-warning" role="alert" aria-live="assertive">
          <p>เกมนี้ถูกเปลี่ยนในแท็บอื่น เพื่อป้องกันข้อมูลทับกัน เกมในแท็บนี้จึงหยุดไว้</p>
          <button className="secondary-button" type="button" onClick={() => window.location.reload()}>
            โหลดเกมล่าสุดใหม่
          </button>
        </section>
      )}
      {storageStatus.phase === 'terminal-save-failed' && (
        <section className="storage-warning" role="alert" aria-live="assertive">
          <p>ผลเกมนี้ยังเก็บไม่สำเร็จ เพื่อป้องกันผลเกมหาย เกมจึงถูกหยุดไว้ชั่วคราว</p>
          <button className="secondary-button" type="button" onClick={() => void retryTerminalPersistence()}>
            ลองบันทึกผลเกมอีกครั้ง
          </button>
        </section>
      )}
      {storageStatus.phase === 'degraded' && (
        <p className="storage-warning" role="status">
          บันทึกในอุปกรณ์นี้มีปัญหา แต่ยังเล่นต่อได้: {storageStatus.error.kind}
        </p>
      )}
      <p className="sr-only" aria-live="polite" aria-atomic="true">{notice}</p>

      {screen === 'home' && (
        <section className="home-screen" aria-labelledby="home-title">
          <div className="home-screen__intro">
            <h1 id="home-title">หมากรุกที่ทุกตัวเป็นคาปีบารา</h1>
            <p className="lede">เริ่มเล่นหรือย้อนดูตาเดินได้ทันทีในเบราว์เซอร์เครื่องนี้</p>
            <div className="home-screen__actions">
              <button className="primary-button" type="button" onClick={() => switchScreen('setup')} disabled={!isHydrated} aria-label="เปิดการตั้งค่าเกม">
                เล่นกับคาปีบารา
              </button>
            </div>
            {!isHydrated && <p className="loading-copy" role="status">กำลังเรียกเกมเดิมจากเครื่องนี้</p>}
          </div>
          <aside className="home-note" aria-label="คุณสมบัติของเกม">
            <ul>
              <li>ไม่ต้องสมัครสมาชิก</li>
              <li>ทุกตาเดินตรวจด้วยกติกาหมากรุกจริง</li>
              <li>มีช่องพิมพ์ตาเดินสำหรับคีย์บอร์ด</li>
            </ul>
          </aside>
        </section>
      )}

      {screen === 'setup' && (
        <section className="setup-screen" aria-labelledby="setup-title">
          <div className="screen-heading">
            <h1 id="setup-title">เลือกคู่ซ้อมของคุณ</h1>
            <p>เริ่มแบบไม่จับเวลา ส่วนเกมกับคาปีบาราย้อนดูตาเดินได้ทุกตา</p>
          </div>
          <div className="setup-layout">
            <fieldset className="setup-fieldset">
              <legend>รูปแบบการเล่น</legend>
              <div className="segmented-choice">
                <button className={setupMode === 'computer' ? 'is-selected' : ''} type="button" onClick={() => setSetupMode('computer')} aria-label="เลือกเล่นกับคาปีบารา">เล่นกับคาปีบารา</button>
                <button className={setupMode === 'hot-seat' ? 'is-selected' : ''} type="button" onClick={() => setSetupMode('hot-seat')} aria-label="เลือกเล่นสองคน">สองคนเครื่องเดียว</button>
              </div>
            </fieldset>
            {setupMode === 'computer' && (
              <>
            <fieldset className="setup-fieldset">
              <legend>คาปีบาราฝั่งตรงข้าม</legend>
              <div className="opponent-options">
                {(Object.keys(OPPONENT_COPY) as BeginnerCapyOpponentId[]).map((id) => (
                  <label className={`opponent-option ${opponent === id ? 'is-selected' : ''}`} key={id}>
                    <input type="radio" name="opponent" value={id} checked={opponent === id} onChange={() => setOpponent(id)} />
                    <span className="opponent-option__title">{OPPONENT_COPY[id].name}</span>
                    <span>{OPPONENT_COPY[id].description}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="setup-fieldset">
              <legend>คุณเล่นสีไหน</legend>
              <div className="segmented-choice" aria-label="เลือกสีของคุณ">
                <button className={humanColor === 'w' ? 'is-selected' : ''} type="button" onClick={() => setHumanColor('w')}>สีขาว</button>
                <button className={humanColor === 'b' ? 'is-selected' : ''} type="button" onClick={() => setHumanColor('b')}>สีดำ</button>
                <button className={humanColor === 'random' ? 'is-selected' : ''} type="button" onClick={() => setHumanColor('random')} aria-label="สุ่มสี">สุ่ม</button>
              </div>
              <p className="field-help">หากเลือกสีดำ คาปีบาราจะเดินก่อน</p>
            </fieldset>
              </>
            )}
            <fieldset className="setup-fieldset">
              <legend>เวลา</legend>
              <div className="segmented-choice setup-time-controls">
                {TIME_CONTROLS.map((option) => (
                  <button
                    key={option.id}
                    className={timeControl === option.id ? 'is-selected' : ''}
                    type="button"
                    onClick={() => setTimeControl(option.id)}
                    aria-label={`เลือกเวลา ${option.label}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <div className="setup-cta">
              {setupMode === 'computer' ? (
                <button className="primary-button" type="button" onClick={beginComputerGame} disabled={!isHydrated}>เริ่มเกมกับ {OPPONENT_COPY[opponent].name}</button>
              ) : (
                <button className="primary-button" type="button" onClick={beginHotSeat} disabled={!isHydrated} aria-label="เริ่มเกมสองคน">เริ่มเกมสองคน</button>
              )}
            </div>
          </div>
        </section>
      )}

      {screen === 'play' && (
        <section className="play-screen" aria-labelledby="play-title">
          <div className="play-status">
            <h1 id="play-title">{describeGameState(state)}</h1>
            <p>{state.mode.kind === 'computer'
              ? 'ต้นกล้ากับนักลาดตระเวนเลือกตาเดินแบบตายตัว ย้อนดูเกมได้ทุกตา'
              : 'ผลัดกันเดินบนเครื่องเดียว'}</p>
          </div>
          <div className="play-layout">
            <section className="board-panel" aria-label="กระดานหมากรุก">
              {capturedMaterial.status === 'ready' ? (
                <CapturedPieceTray
                  color={topTrayColor}
                  pieces={capturedMaterial.lostByColor[topTrayColor]}
                  theme={pieceTheme}
                />
              ) : (
                <p className="captured-tray captured-tray--unavailable" role="status">
                  ยังตรวจรายการตัวหมากที่เสียไม่ได้
                </p>
              )}
              <CapyBoard
                projection={projection}
                onMoveIntent={onGameMoveIntent}
                label="กระดานหมากรุก CapyChess"
                battleCue={battleCue}
                pieceTheme={pieceTheme}
                actionArtRuntime={pieceTheme === 'capy' ? actionArtRuntime : undefined}
                onKeyboardFeedback={announceKeyboardFeedback}
              />
              {capturedMaterial.status === 'ready' && (
                <CapturedPieceTray
                  color={bottomTrayColor}
                  pieces={capturedMaterial.lostByColor[bottomTrayColor]}
                  theme={pieceTheme}
                />
              )}
              <div className="board-toolbar">
                <button
                  className="secondary-button piece-theme-toggle"
                  type="button"
                  data-piece-theme-toggle
                  aria-pressed={pieceTheme === 'standard'}
                  aria-label={pieceTheme === 'capy'
                    ? 'สลับเป็นตัวหมากมาตรฐาน'
                    : 'สลับกลับเป็นตัวหมากคาปีบารา'}
                  onClick={() => {
                    const next = pieceTheme === 'capy' ? 'standard' : 'capy';
                    setPieceTheme(next);
                    setNotice(next === 'capy'
                      ? 'เปลี่ยนเป็นตัวหมากคาปีบาราแล้ว'
                      : 'เปลี่ยนเป็นตัวหมากมาตรฐานแล้ว ปิดแอนิเมชันการต่อสู้');
                  }}
                >
                  ตัวหมาก: {pieceTheme === 'capy' ? 'คาปีบารา' : 'มาตรฐาน'}
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  data-board-flip
                  onClick={() => setOrientation((value) => value === 'white' ? 'black' : 'white')}
                >
                  กลับกระดาน
                </button>
                {undoAvailable && <button className="secondary-button" type="button" onClick={undoLearn}>ย้อนตาเดิน</button>}
                {state.review && <button className="secondary-button" type="button" onClick={exitReview}>กลับเกมสด</button>}
              </div>
            </section>
            <aside className="game-sidebar" aria-label="ตาเดินและคำสั่ง">
              <section className="turn-card">
                <strong>{describeGameState(state)}</strong>
                {projection.lastMove && (
                  <p className="last-move-summary" data-last-move-summary>
                    {describeLastMove(projection.lastMove)}
                  </p>
                )}
              </section>
              <GameClocks state={state} nowMs={clockNowMs} />
              <form className="move-entry" onSubmit={submitTextMove}>
                <label htmlFor="move-entry">พิมพ์ตาเดิน</label>
                <div>
                  <input
                    id="move-entry"
                    value={moveInput}
                    onChange={(event) => setMoveInput(event.target.value)}
                    placeholder="e2e4"
                    inputMode="text"
                    autoComplete="off"
                    disabled={!projection.interactive}
                    aria-invalid={moveInputError !== ''}
                    aria-describedby={moveInputError === '' ? 'move-entry-help' : 'move-entry-help move-entry-error'}
                  />
                  <button className="primary-button" type="submit" disabled={!projection.interactive}>เดิน</button>
                </div>
                <p id="move-entry-help">ใช้ e2e4 หรือ e7e8q สำหรับเลื่อนขั้น</p>
                {moveInputError !== '' && (
                  <p id="move-entry-error" className="move-entry__error" role="alert">{moveInputError}</p>
                )}
              </form>
              {!state.result.terminal && !state.review && !state.promotionPending && (
                <div className="inline-actions game-actions">
                  {state.mode.kind === 'hot-seat' && isHydrated && (
                    <button className="secondary-button" type="button" onClick={() => setDrawOfferOpen(true)} aria-label="ขอเสมอ">ขอเสมอ</button>
                  )}
                  <button className="secondary-button" type="button" onClick={() => setResignDialogOpen(true)} aria-label="ยอมแพ้">ยอมแพ้</button>
                </div>
              )}
              <section className="move-list" aria-labelledby="move-list-title">
                <h2 id="move-list-title">ตาเดิน</h2>
                {displayedMoves.length === 0 ? (
                  <p>เกมเพิ่งเริ่ม เลือกตัวหมากบนกระดานได้เลย</p>
                ) : (
                  <ol>
                    {displayedMoves.map((move, index) => (
                      <li key={`${move.uci}-${index}`}>
                        <button
                          type="button"
                          onClick={() => selectReviewPly(index + 1)}
                          disabled={state.clock !== undefined && !state.result.terminal}
                          title={state.clock !== undefined && !state.result.terminal
                            ? 'ทบทวนตำแหน่งได้หลังเกมจับเวลาจบ'
                            : undefined}
                        >
                          {move.san}
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </aside>
          </div>
          {state.result.terminal && (
            <ResultCard
              state={state}
              onRematch={beginRematch}
              onChangeSetup={() => switchScreen('setup')}
              onReview={() => switchScreen('review')}
            />
          )}
          {state.promotionPending && (
            <PromotionDialog onChoose={choosePromotion} onCancel={cancelPromotion} />
          )}
          {resignDialogOpen && !state.result.terminal && (
            <DecisionDialog
              dialogId="resign"
              title="ยืนยันการยอมแพ้"
              description="เกมนี้จะจบทันที และคุณยังย้อนดูทุกตาเดินได้"
              confirmLabel="ยืนยันยอมแพ้"
              cancelLabel="เล่นต่อ"
              onConfirm={confirmResign}
              onCancel={() => setResignDialogOpen(false)}
            />
          )}
          {drawOfferOpen && isHydrated && state.mode.kind === 'hot-seat' && !state.result.terminal && !state.review && !state.promotionPending && (
            <DecisionDialog
              dialogId="draw-offer"
              title="อีกฝ่ายเสนอให้เสมอ"
              description="ส่งเครื่องให้ผู้เล่นอีกฝ่าย แล้วเลือกว่าจะยอมรับผลเสมอหรือเล่นต่อ"
              confirmLabel="ยอมรับผลเสมอ"
              cancelLabel="ปฏิเสธและเล่นต่อ"
              onConfirm={confirmDrawAgreement}
              onCancel={() => setDrawOfferOpen(false)}
            />
          )}
        </section>
      )}

      {screen === 'puzzle' && (
        <section className="puzzle-screen" aria-labelledby="puzzle-title">
          <div className="screen-heading">
            <h1 id="puzzle-title">{puzzle.puzzle.teaching?.title ?? 'โจทย์คาปีบารา'}</h1>
            <p>{puzzle.puzzle.teaching?.objective ?? 'หาตาเดินที่ดีที่สุด'}</p>
          </div>
          <div className="puzzle-layout">
            <section className="board-panel">
              <CapyBoard
                key={activePuzzleId}
                projection={puzzleProjection}
                onMoveIntent={onPuzzleMoveIntent}
                label="กระดานโจทย์ CapyChess"
                onKeyboardFeedback={announceKeyboardFeedback}
              />
            </section>
            <aside className="puzzle-sidebar">
              <section className="puzzle-progress-picker" aria-labelledby="puzzle-picker-title">
                <div className="puzzle-progress-picker__heading">
                  <h2 id="puzzle-picker-title">เลือกโจทย์</h2>
                  <p>แก้แล้ว {solvedPuzzleCount}/{puzzleStates.length} ข้อ</p>
                </div>
                <div className="puzzle-progress-picker__choices" aria-label="โจทย์ฝึกทั้งหมด">
                  {puzzleStates.map((candidate, index) => {
                    const selected = candidate.puzzle.id === activePuzzleId;
                    return (
                      <button
                        key={candidate.puzzle.id}
                        className={`puzzle-progress-choice ${selected ? 'is-selected' : ''}`}
                        type="button"
                        data-puzzle-id={candidate.puzzle.id}
                        aria-pressed={selected}
                        aria-label={`เลือกโจทย์ ${index + 1}: ${candidate.puzzle.teaching?.title ?? 'โจทย์คาปีบารา'}`}
                        onClick={() => selectPuzzle(candidate.puzzle.id)}
                        disabled={puzzleControlsLocked}
                      >
                        <span>โจทย์ {index + 1}</span>
                        <span className="puzzle-progress-choice__state">{describePuzzleProgress(candidate)}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
              {puzzleProgressStorageStatus.phase === 'loading' && (
                <p className="puzzle-storage-status" role="status">กำลังเปิดความคืบหน้าในเครื่องนี้</p>
              )}
              {puzzleProgressStorageStatus.phase === 'degraded' && (
                <section
                  className="puzzle-storage-status is-warning"
                  role={puzzleProgressStorageStatus.recovery === 'invalid-record' ? 'alert' : 'status'}
                  data-puzzle-progress-invalid-recovery={
                    puzzleProgressStorageStatus.recovery === 'invalid-record' ? '' : undefined
                  }
                >
                  <p>
                    {puzzleProgressStorageStatus.recovery === 'invalid-record'
                      ? 'พบความคืบหน้าโจทย์ที่ใช้กับเวอร์ชันนี้ไม่ได้ ข้อมูลเดิมยังไม่ได้เปลี่ยน'
                      : 'เก็บความคืบหน้าในเครื่องนี้ไม่ได้ จึงจะไม่แทนที่ข้อมูลเดิม'}
                  </p>
                  {puzzleProgressStorageStatus.recovery === 'invalid-record' && (
                    <button
                      className="text-button"
                      type="button"
                      onClick={() => setPuzzleProgressResetDialogOpen(true)}
                    >
                      เริ่มความคืบหน้าโจทย์ใหม่
                    </button>
                  )}
                </section>
              )}
              {puzzleProgressStorageStatus.phase === 'conflict' && (
                <section
                  className="puzzle-storage-status is-warning"
                  data-puzzle-progress-conflict
                  role="alert"
                >
                  <p>ความคืบหน้าโจทย์ถูกเปลี่ยนจากแท็บอื่น</p>
                  <button className="text-button" type="button" onClick={() => void reloadPuzzleProgress()}>
                    โหลดความคืบหน้าล่าสุด
                  </button>
                </section>
              )}
              <p className="puzzle-feedback" role="status">
                {puzzle.result === 'ready' && 'หาตาเดินแรก แล้วเลือกช่องปลายทาง'}
                {puzzle.result === 'correct' && 'ดีมาก ตาเดินต่อไปกำลังรอคุณ'}
                {puzzle.result === 'try-again' && 'ยังไม่ใช่ ลองมองภัยคุกคามอีกครั้ง'}
                {puzzle.result === 'solved' && 'แก้โจทย์สำเร็จแล้ว'}
                {puzzle.result === 'revealed' && 'นี่คือแนวคำตอบทั้งหมด'}
              </p>
              {puzzleProjection.lastMove && (
                <p className="last-move-summary" data-last-move-summary>
                  {describeLastMove(puzzleProjection.lastMove)}
                </p>
              )}
              {puzzle.hint && <p className="hint-box">คำใบ้ {puzzle.hint.stage}/{puzzle.hint.totalStages}: {puzzle.hint.text}</p>}
              <form className="move-entry" onSubmit={submitPuzzleTextMove}>
                <label htmlFor="puzzle-move-entry">พิมพ์ตาเดินโจทย์</label>
                <div>
                  <input
                    id="puzzle-move-entry"
                    value={puzzleMoveInput}
                    onChange={(event) => setPuzzleMoveInput(event.target.value)}
                    placeholder="g1f3"
                    autoComplete="off"
                    disabled={!puzzleCanAttempt}
                    aria-invalid={puzzleInputError !== ''}
                    aria-describedby={puzzleInputError === '' ? undefined : 'puzzle-move-entry-error'}
                  />
                  <button className="primary-button" type="submit" disabled={!puzzleCanAttempt}>เดิน</button>
                </div>
              </form>
              {puzzleInputError !== '' && (
                <p id="puzzle-move-entry-error" className="puzzle-input-error" role="alert">{puzzleInputError}</p>
              )}
              <div className="stacked-actions">
                <button className="secondary-button" type="button" onClick={() => reduceCurrentPuzzle({ type: 'request-hint' })} disabled={!puzzleCanRequestHint}>ขอคำใบ้</button>
                <button className="secondary-button" type="button" onClick={() => reduceCurrentPuzzle({ type: 'reveal-solution' })} disabled={!puzzleCanReveal}>ดูคำตอบ</button>
                <button className="text-button" type="button" onClick={() => reduceCurrentPuzzle({ type: 'retry' })} disabled={puzzleControlsLocked}>เริ่มโจทย์นี้ใหม่</button>
              </div>
              <p className="puzzle-provenance">โจทย์สร้างโดย CapyChess เพื่อการฝึก ไม่ดึงข้อมูลผู้เล่นหรือเชื่อมบัญชี</p>
            </aside>
          </div>
          {puzzlePromotion && (
            <PromotionDialog
              onChoose={(piece) => applyPuzzleUci(`${puzzlePromotion.from}${puzzlePromotion.to}${piece}`)}
              onCancel={() => setPuzzlePromotion(undefined)}
            />
          )}
          {puzzleProgressResetDialogOpen && (
            <PuzzleProgressResetDialog
              onConfirm={() => void resetInvalidPuzzleProgress()}
              onCancel={() => setPuzzleProgressResetDialogOpen(false)}
            />
          )}
        </section>
      )}

      {screen === 'review' && (
        <section className="review-screen" aria-labelledby="review-title">
          <div className="screen-heading">
            <h1 id="review-title">ทบทวนเกมของคุณ</h1>
            <p>{state.clock !== undefined && !state.result.terminal
              ? 'เกมจับเวลายังเดินอยู่ เปิดตำแหน่งเดิมได้หลังเกมจบ'
              : 'เลือกตาเดินเพื่อเปิดตำแหน่งเดิมแบบอ่านอย่างเดียว แล้วกลับมาเล่นสดได้เสมอ'}</p>
          </div>
          <div className="review-card">
            <p>{state.acceptedMoves.length === 0 ? 'ยังไม่มีตาเดินในเกมปัจจุบัน' : `เกมปัจจุบันมี ${state.acceptedMoves.length} ตาเดิน`}</p>
            <div className="review-moves">
              {state.acceptedMoves.map((move, index) => (
                <button
                  key={`${move.uci}-${index}`}
                  type="button"
                  disabled={state.clock !== undefined && !state.result.terminal}
                  onClick={() => { selectReviewPly(index + 1); setScreen('play'); }}
                >
                  {index + 1}. {move.san}
                </button>
              ))}
            </div>
            <section className="review-export" aria-labelledby="review-export-title">
              <div>
                <h2 id="review-export-title">เก็บเกมเป็น PGN</h2>
                <p>คัดลอกหรือดาวน์โหลดบันทึกเกมมาตรฐาน เพื่อนำไปเปิดในแอปหมากรุกอื่นได้</p>
              </div>
              <div className="inline-actions">
                <button className="secondary-button" type="button" onClick={() => void copyReviewPgn()}>
                  คัดลอก PGN
                </button>
                <button className="secondary-button" type="button" onClick={downloadReviewPgn}>
                  ดาวน์โหลด PGN
                </button>
              </div>
              {reviewExportStatus && (
                <p
                  className={`review-export__status ${reviewExportStatus.kind === 'error' ? 'is-error' : ''}`}
                  role="status"
                >
                  {reviewExportStatus.message}
                </p>
              )}
            </section>
            <div className="inline-actions">
              <button className="primary-button" type="button" onClick={() => switchScreen('play')}>กลับไปที่กระดาน</button>
              <button className="secondary-button" type="button" onClick={() => switchScreen('setup')}>เริ่มเกมใหม่</button>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
